import React, { useState } from 'react';
import { useDropzone, FileRejection, FileError } from 'react-dropzone';
import { upload } from './uploadFile';
import { injectValue, isCallable } from '../_utils';
import { UploadContext } from './UploadContext';
import { UploadProps } from './interface';

function isPromise(p: any): p is Promise<any> {
  return p && typeof p === 'object' && typeof p.then === 'function';
}

const Upload: React.FC<UploadProps> = props => {
  const {
    action = '',
    accept,
    maxSize,
    multiple = false,
    directory = false,
    headers,
    data,
    name = 'file',
    withCredentials,
    beforeUpload = (_, __, isAccepted) => isAccepted,
    onStart = () => null,
    onProgress,
    onSuccess,
    onError,
    children,
    className,
    style,
  } = props;

  let isMultiple = multiple;
  if (directory) {
    isMultiple = true;
  }

  const [dragging, setDragging] = useState(false);

  const { getInputProps, getRootProps, open } = useDropzone({
    accept: accept as any,
    maxSize,
    multiple: isMultiple,
    noClick: true,
    noKeyboard: true,
    onDrop: uploadFiles,
    onDragEnter: () => setDragging(true),
    onDragLeave: () => setDragging(false),
  });

  function uploadFiles(acceptedFiles: File[] = [], fileRejections: FileRejection[]) {
    const rejectedFiles = fileRejections.map(r => r.file);
    const fileList = [...acceptedFiles, ...rejectedFiles];

    acceptedFiles.forEach(file => {
      uploadFile(file, fileList, true);
    });

    fileRejections.forEach(({ file, errors }) => {
      uploadFile(file, fileList, false, errors);
    });

    setDragging(false);
  }

  function uploadFile(
    file: File,
    fileList: File[],
    isAccepted: boolean,
    reasons: FileError[] = [],
  ) {
    const couldUpload = beforeUpload(file, fileList, isAccepted, reasons);
    if (isPromise(couldUpload)) {
      couldUpload.then(() => request(file)).catch(() => null);
    } else {
      if (!couldUpload) {
        return;
      }
      setTimeout(() => request(file), 0);
    }
  }

  async function request(file: File) {
    let uploadAction: string;
    if (typeof action === 'function') {
      uploadAction = await action(file);
    } else {
      uploadAction = action;
    }

    const xhr = upload({
      action: uploadAction,
      filename: name,
      file,
      data: injectValue(data)(file),
      headers: injectValue(headers)(file),
      withCredentials,
      onProgress,
      onSuccess,
      onError,
    });

    onStart(file, { xhr });
  }

  return (
    <UploadContext.Provider value={{ open, getDraggerProps: getRootProps, isDragging: dragging }}>
      <span className={className} style={style}>
        <input
          {...getInputProps()}
          {...{
            directory: directory ? 'directory' : undefined,
            webkitdirectory: directory ? 'webkitdirectory' : undefined,
          }}
        />
        {isCallable(children) ? (
          children({
            open,
            getDraggerProps: getRootProps,
            isDragging: dragging,
          })
        ) : (
          <span onClick={open}>{children}</span>
        )}
      </span>
    </UploadContext.Provider>
  );
};

export default Upload;
